export class Color {
    r: number;
    g: number;
    b: number;
    a: number;

    constructor(rOrCss: number | string = 0, g: number = 0, b: number = 0, a: number = 1) {
        if (typeof rOrCss === 'string') {
            const [r, g, b, a] = Color.parseCssColor(rOrCss);
            this.r = r;
            this.g = g;
            this.b = b;
            this.a = a;
        } else {
            this.r = rOrCss;
            this.g = g;
            this.b = b;
            this.a = a;
        }
    }

    private static parseCssColor(cssColor: string): [number, number, number, number] {
        // Match rgba or rgb format
        let rgba = cssColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([01]?\.?\d*))?\)/);
        if (rgba) {
            const r = parseInt(rgba[1], 10);
            const g = parseInt(rgba[2], 10);
            const b = parseInt(rgba[3], 10);
            const a = rgba[4] !== undefined ? parseFloat(rgba[4]) : 1;
            return [r, g, b, a];
        }

        // Match hex format: #rgb, #rgba, #rrggbb, #rrggbbaa
        let hex = cssColor.match(/^#([a-fA-F0-9]{3,8})$/);
        if (hex) {
            let hexValue = hex[1];
            if (hexValue.length === 3) {
                hexValue = hexValue.split('').map(x => x + x).join('');
            }
            if (hexValue.length === 6) {
                hexValue += 'ff'; // Add alpha if not present
            }
            const bigint = parseInt(hexValue, 16);
            const r = (bigint >> 24) & 255;
            const g = (bigint >> 16) & 255;
            const b = (bigint >> 8) & 255;
            const a = (bigint & 255) / 255;
            return [r, g, b, a];
        }

        throw new Error('Invalid CSS color format');
    }

    toString() {
        return `rgba(${this.r}, ${this.g}, ${this.b}, ${this.a})`;
    }

    withAlpha(a: number): Color {
        return new Color(this.r, this.g, this.b, a);
    }

    brighten(amount: number): Color {
        const hsl = rgbToHsl(this.r, this.g, this.b);

        const [r, g, b] = hslToRgb(hsl[0], hsl[1], hsl[2] + amount);
        return new Color(r, g, b, this.a);
    }

    darken(amount: number): Color {
        return this.brighten(-amount);
    }

    lightness(): number {
        const [h, s, l] = rgbToHsl(this.r, this.g, this.b);
        return l;
    }

    luminosity() {
        const lum = {
            r: component(this.r),
            g: component(this.g),
            b: component(this.b),
        };

        function component(c: number) {
            const chan = c / 255;
            return (chan <= 0.03928) ? chan / 12.92 : Math.pow(((chan + 0.055) / 1.055), 2.4);
        }

        return 0.2126 * lum.r + 0.7152 * lum.g + 0.0722 * lum.b;
    }

    complement(): Color {
        const [h, s, l] = rgbToHsl(this.r, this.g, this.b);
        const hCmp = (h + 180) % 360;
        return new HSLColor(hCmp, s, l, this.a);
    }

    gsComplement(): Color {
        const [h, s, l] = rgbToHsl(this.r, this.g, this.b);

        const gs = l <= 60 ? 255 : 0;
        return new Color(gs, gs, gs, this.a);
    }
}

export class HSLColor extends Color {
    constructor(h: number = 0, s: number = 0, l: number = 0, a: number = 1) {
        const [r, g, b] = hslToRgb(h, s, l);
        super(r, g, b, a);
    }
}

export const Colors = {
    Red: new Color(255, 0, 0),
    Green: new Color(0, 255, 0),
    Blue: new Color(0, 0, 255),
    Gray: new Color(128, 128, 128),
    Black: new Color(0, 0, 0),
};

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
    r /= 255, g /= 255, b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    let h: number = 0;
    let s: number = 0;

    if (max == min) {
        h = s = 0; // achromatic
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }
        h /= 6;
    }

    return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
    s /= 100;
    l /= 100;
    let r: number, g: number, b: number;

    if (s === 0) {
        r = g = b = l; // achromatic
    } else {
        const hue2rgb = (p: number, q: number, t: number): number => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };

        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;

        r = hue2rgb(p, q, h / 360 + 1 / 3);
        g = hue2rgb(p, q, h / 360);
        b = hue2rgb(p, q, h / 360 - 1 / 3);
    }

    return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}
